import { displayLocaleTime, OptionsField } from 'gossamer-unified';
import { getConfig } from 'gossamer-front-end';
import { EventExtendedView, TicketItem } from 'cb-model';

export type EventDate = {
  start: string;
  end: string;
  recurrence?: {
    pattern: string;
    reschedule?: {
      start: string;
      end: string;
    }[];
  };
};

export type TicketItemWithBookings = {
  id: number;
  title: string;
  price: number;
  quantity: number;
  bookings: number;
};

export function addDays(base: Date, days: number): Date {
  const copy = new Date(base);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function formatEventDate(date: EventDate, includeTime: boolean = true): string {
  if (!date) return 'On-Demand';
  const { start } = date;

  // If we can't parse the date properly, return "Upcoming"
  const parsedStart = new Date(start);
  if (isNaN(parsedStart.getTime())) return 'Upcoming';
  return formatSingleDate(parsedStart, includeTime);
}

export function formatEventUrl(title: string, id: number | string) {
  return '/website/events/' + makeEventUrlSlug(title, id);
}

export function formatSingleDateStr(dateStr: string, includeTime: boolean = true): string {
  if (!dateStr) return 'On-Demand';
  const date = new Date(dateStr);
  return formatSingleDate(date, includeTime);
}

export function formatSingleDate(date: Date, includeTime: boolean = true): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = addDays(today, 1);

  if (includeTime) {
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    const timeString = date.toLocaleString(undefined, timeOptions);

    if (date >= today && date < tomorrow) {
      return `Today ${timeString}`;
    } else if (date >= tomorrow && date < addDays(tomorrow, 1)) {
      return `Tomorrow ${timeString}`;
    }

    const fullDateOptions: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };
    return date.toLocaleString(undefined, fullDateOptions).replace(',', '');
  } else {
    const dateOnlyOptions: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    };

    if (date >= today && date < tomorrow) {
      return 'Today';
    } else if (date >= tomorrow && date < addDays(tomorrow, 1)) {
      return 'Tomorrow';
    }

    return date.toLocaleString(undefined, dateOnlyOptions).replace(',', '');
  }
}

export const formatTimeRange = (start: Date | string, end: Date | string): string => {
  const startDate = typeof start === 'string' ? new Date(start) : start;
  const endDate = typeof end === 'string' ? new Date(end) : end;
  return [displayLocaleTime(startDate), displayLocaleTime(endDate)].join(' - ');
};

export const isGenericMode = () => getConfig('generic-mode') === 'true';

export const ifGenericMode = (genericValue: any, nonGenericValue: any) =>
  isGenericMode() ? genericValue : nonGenericValue;

export function makeEventUrlSlug(title: string, id: number | string) {
  return (
    title
      .trim() // Remove leading/trailing whitespace
      .toLowerCase() // Convert to lowercase
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') +
    '-' +
    id
  );
}

export function extractEventId(url: string): number {
  const lastDashIndex = url.lastIndexOf('-');
  if (lastDashIndex === -1) {
    throw new Error('No dash found in the URL to extract the event ID.');
  }

  const idPart = url.substring(lastDashIndex + 1);
  const id = parseInt(idPart, 10);

  if (isNaN(id)) {
    throw new Error('The event ID after the last dash is not a valid number.');
  }

  return id;
}

export const getEventType = (eventType: string) =>
  (EventExtendedView.fields.type as OptionsField).options.find((opt) => opt.id === eventType)?.label || eventType;

export const getTicketsLeftFromTicketData = (tickets: TicketItemWithBookings[]) =>
  tickets.reduce((prev, curr) => prev + Math.max(0, curr.quantity - curr.bookings), 0);

export const getCheapestPriceFormatted = (tickets: TicketItem[]) => {
  const cheapestPrice =
    tickets && tickets.reduce((cheapest, ticket) => (ticket.price < cheapest ? ticket.price : cheapest), Infinity);
  return cheapestPrice === 0 ? 'Free' : Number.isFinite(cheapestPrice) ? `From £${cheapestPrice.toFixed(2)}` : '';
};
